import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Printer } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import leiloesService from '../../services/leiloesService';
import { ordemEntradaService } from '../../services/ordemEntradaService';
import { lotesService } from '../../services/lotesService';
import catalogoDetalhadoConfigService from '../../services/catalogoDetalhadoConfigService';
import catalogoConfigService from '../../services/catalogoConfigService';
import CatalogoBase from '../../components/catalogos/CatalogoBase';
import CatalogoLeilaoButton from '../../components/catalogos/CatalogoLeilaoButton';

const formatarPercentual = (pct) => Number.isInteger(pct) ? pct : parseFloat(pct.toFixed(1));

const TODAS_COLUNAS = [
  { id: 'ordem',         label: 'O.D',              width: 'w-10'  },
  { id: 'lote',          label: 'Lote',             width: 'w-12'  },
  { id: 'vendedor',      label: 'Vendedor/Fazenda', width: 'w-40'  },
  { id: 'fazenda',       label: 'Fazenda',          width: 'w-32'  },
  { id: 'quantidade',    label: 'Qtde',             width: 'w-10'  },
  { id: 'tipo',          label: 'Tipo',             width: 'w-20'  },
  { id: 'raca',          label: 'Raça',             width: 'w-20'  },
  { id: 'sexo',          label: 'Sexo',             width: 'w-10'  },
  { id: 'idade',         label: 'Idade',            width: 'w-12'  },
  { id: 'peso',          label: 'Peso',             width: 'w-14'  },
  { id: 'curral',        label: 'Curral',           width: 'w-14'  },
  { id: 'observacoes',   label: 'Observações',      width: 'w-32'  },
  { id: 'valor_inicial', label: 'Valor Inicial',    width: 'w-20'  },
  { id: 'pelagem',          label: 'Pelagem',       width: 'w-20'  },
  { id: 'registro',         label: 'Registro',      width: 'w-20'  },
  { id: 'percentual_venda', label: '% Venda',       width: 'w-12'  },
  { id: 'localizacao',      label: 'Localização',   width: 'w-24'  },
];

// Fluxo cavalo: identidade do animal antes do vendedor, igual aos outros
// catálogos.
const TODAS_COLUNAS_CAVALO = [
  { id: 'ordem',         label: 'O.D',              width: 'w-10'  },
  { id: 'lote',          label: 'Lote',             width: 'w-12'  },
  { id: 'animal',        label: 'Animal',           width: 'w-52'  },
  { id: 'registro',      label: 'Registro',         width: 'w-20'  },
  { id: 'raca',          label: 'Raça',             width: 'w-20'  },
  { id: 'data_nascimento', label: 'Nasc.',          width: 'w-20'  },
  { id: 'pai',           label: 'Pai',              width: 'w-20'  },
  { id: 'mae',           label: 'Mãe',              width: 'w-20'  },
  { id: 'idade',         label: 'Idade',            width: 'w-12'  },
  { id: 'sexo',          label: 'Sexo',             width: 'w-10'  },
  { id: 'tipo',          label: 'Tipo',             width: 'w-20'  },
  { id: 'peso',          label: 'Peso',             width: 'w-14'  },
  { id: 'curral',        label: 'Curral',           width: 'w-14'  },
  { id: 'vendedor',      label: 'Vendedor/Fazenda', width: 'w-40'  },
  { id: 'fazenda',       label: 'Fazenda',          width: 'w-32'  },
  { id: 'observacoes',   label: 'Observações',      width: 'w-32'  },
  { id: 'valor_inicial', label: 'Valor Inicial',    width: 'w-20'  },
  { id: 'pelagem',           label: 'Pelagem',      width: 'w-20'  },
  { id: 'percentual_venda',  label: '% Venda',      width: 'w-12'  },
  { id: 'localizacao',       label: 'Localização',  width: 'w-24'  },
];

const DEFAULT_COLUNAS_ATIVAS = ['ordem', 'lote', 'vendedor', 'quantidade', 'tipo', 'raca', 'sexo', 'idade', 'observacoes'];
const DEFAULT_COLUNAS_ATIVAS_CAVALO = ['lote', 'animal', 'raca', 'data_nascimento', 'pai', 'mae', 'vendedor', 'observacoes'];

const CatalogoDetalhado = () => {
  const { leilaoId } = useParams();
  const navigate = useNavigate();
  const { organizadorAtivo } = useOrganizador();
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo';
  const [leilao, setLeilao] = useState(null);
  const [leiloes, setLeiloes] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuracao, setConfiguracao] = useState({
    colunas_ativas: DEFAULT_COLUNAS_ATIVAS,
    mostrar_totais: true,
    lotes_por_pagina: 32,
    observacao_sempre_abaixo: false,
  });
  const [modoPeso, setModoPeso] = useState('peso_arroba');

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarLeiloes();
    }
  }, [organizadorAtivo]);

  useEffect(() => {
    if (leilaoId && organizadorAtivo?.id) {
      carregarDados();
    }
  }, [leilaoId, organizadorAtivo]);

  const carregarLeiloes = async () => {
    try {
      const data = await leiloesService.getByOrganizador(organizadorAtivo.id);
      setLeiloes(data || []);
    } catch (error) {
      console.error('Erro ao carregar leilões:', error);
    }
  };

  const handleLeilaoChange = (novoLeilaoId) => {
    if (novoLeilaoId !== leilaoId) {
      navigate(`/catalogos/detalhado/${novoLeilaoId}`);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [leilaoData, ordemData, lotesCompletos, config, configLeilao] = await Promise.all([
        leiloesService.getById(organizadorAtivo.id, leilaoId),
        ordemEntradaService.listarOrdem(leilaoId).catch(() => []),
        lotesService.listarLotesPorLeilao(organizadorAtivo.id, leilaoId),
        catalogoDetalhadoConfigService.getConfig(organizadorAtivo.id).catch(() => null),
        catalogoConfigService.getConfig(organizadorAtivo.id).catch(() => null),
      ]);

      const colunasDefault = isFluxoCavalo ? DEFAULT_COLUNAS_ATIVAS_CAVALO : DEFAULT_COLUNAS_ATIVAS;
      if (config) {
        const colunasAtivas = Array.isArray(config.colunas_ativas)
          ? config.colunas_ativas
          : (typeof config.colunas_ativas === 'string' ? JSON.parse(config.colunas_ativas) : colunasDefault);
        setConfiguracao({
          colunas_ativas: colunasAtivas,
          mostrar_totais: config.mostrar_totais !== false,
          lotes_por_pagina: config.lotes_por_pagina || 32,
          observacao_sempre_abaixo: !!config.observacao_sempre_abaixo,
        });
      } else {
        setConfiguracao(prev => ({ ...prev, colunas_ativas: colunasDefault }));
      }
      if (configLeilao?.modo_peso) {
        setModoPeso(configLeilao.modo_peso);
      }

      let lotesComDados;

      if (ordemData && ordemData.length > 0) {
        const idsNaOrdem = new Set(ordemData.map(o => o.lote_id));
        const lotesOrdenados = ordemData
          .map(itemOrdem => ({
            ...lotesCompletos.find(l => l.id === itemOrdem.lote_id),
            ...itemOrdem,
          }))
          .sort((a, b) => a.posicao - b.posicao);

        const lotesFora = lotesCompletos
          .filter(l => !idsNaOrdem.has(l.id))
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0));

        const offset = lotesOrdenados.length;
        lotesComDados = [
          ...lotesOrdenados,
          ...lotesFora.map((lote, i) => ({ ...lote, posicao: offset + i + 1, lote_id: lote.id, lote_numero: lote.numero_lote })),
        ];
      } else {
        lotesComDados = lotesCompletos
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0))
          .map((lote, index) => ({ ...lote, posicao: index + 1 }));
      }

      setLeilao(leilaoData);
      setLotes(lotesComDados);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Idade calculada a partir da data de nascimento do animal (fluxo cavalo —
  // LoteAnimal não tem cadastro de Idade como o lote de gado). Mesma lógica
  // de reports/animal_spec_utils.py:idade_label().
  const idadeLabel = (dataNascimento) => {
    if (!dataNascimento) return '-';
    const nasc = new Date(dataNascimento);
    const hoje = new Date();
    let meses = (hoje.getFullYear() - nasc.getFullYear()) * 12 + (hoje.getMonth() - nasc.getMonth());
    if (hoje.getDate() < nasc.getDate()) meses -= 1;
    meses = Math.max(0, meses);
    if (meses < 24) return `${meses} MES${meses !== 1 ? 'ES' : ''}`;
    const anos = Math.floor(meses / 12);
    return `${anos} ANO${anos !== 1 ? 'S' : ''}`;
  };

  const SEXO_LABEL = { M: 'Macho', F: 'Fêmea', C: 'Castrado' };
  const ehCavalo = (lote) => !lote.tipo_animal;
  const linhasExpandidas = lotes.flatMap((lote) => {
    const animais = ehCavalo(lote) ? (lote.animais || []) : [];
    if (animais.length > 0) {
      return animais.map((animal) => ({ ...lote, _animal: animal }));
    }
    return [{ ...lote, _animal: null }];
  });
  const corPorLote = {};
  let _proxPar = true;
  linhasExpandidas.forEach((item) => {
    if (!(item.id in corPorLote)) {
      corPorLote[item.id] = _proxPar;
      _proxPar = !_proxPar;
    }
  });

  const temPeso = linhasExpandidas.some(item => item._animal ? item._animal.peso : (item.peso_medio || item.peso));
  const temDestaque = (item) => item._animal && Math.abs(parseFloat(item._animal.percentual_venda ?? 100) - 100) > 0.001;

  // Filtrar colunas respeitando a configuração salva
  const todasColunas = isFluxoCavalo ? TODAS_COLUNAS_CAVALO : TODAS_COLUNAS;
  const colunas = todasColunas.filter(col => {
    if (col.id === 'peso' && !temPeso) return false;
    return configuracao.colunas_ativas.includes(col.id);
  }).map(col => col.id === 'peso'
    ? { ...col, label: modoPeso === 'peso_arroba' ? 'Peso(kg)/@' : 'Peso(Kg)' }
    : col
  );

  const obsSempreAbaixo = colunas.some(c => c.id === 'observacoes') && !!configuracao.observacao_sempre_abaixo;
  const colunasTabela = (obsSempreAbaixo || isFluxoCavalo)
    ? colunas.filter(c => c.id !== 'observacoes')
    : colunas;

  // Observação + localização, mesma linha. Gado: observação do lote (de
  // sempre). Cavalo: observação do PRÓPRIO ANIMAL (informacoes) + sua
  // localização.
  const obsMaisLocalizacao = (item) => {
    if (!item._animal) {
      const obs = getObsTexto(item);
      return obs !== '-' ? obs : '';
    }
    const obs = (item._animal.informacoes || '').trim();
    const local = (item._animal.localizacao || '').trim();
    const partes = [];
    if (obs) partes.push(`OBS. ${obs}`);
    if (local) partes.push(`LOCAL ${local.toUpperCase()}`);
    return partes.join('  ·  ');
  };

  const getObsTexto = (item) => {
    let obs = item.observacoes || '';
    const criasF = item.qtd_crias_femeas || 0;
    const criasM = item.qtd_crias_machos || 0;
    if (criasF > 0 || criasM > 0) {
      const partes = [];
      if (criasF > 0) partes.push(`F:${criasF}`);
      if (criasM > 0) partes.push(`M:${criasM}`);
      const criasTxt = `Crias: ${partes.join(' ')}`;
      obs = obs ? `${criasTxt} | ${obs}` : criasTxt;
    }
    return obs || '-';
  };

  const renderCelula = (item, colunaId) => {
    switch (colunaId) {
      case 'ordem':
        return <td key={colunaId} className="border border-gray-300 px-1 py-2 text-center font-bold">{item.posicao}</td>;
      case 'lote':
        return <td key={colunaId} className="border border-gray-300 px-1 py-2 font-medium">{item.numero_lote}</td>;
      case 'vendedor':
        return (
          <td key={colunaId} className="border border-gray-300 px-2 py-2">
            <div className="text-sm">
              <div className="font-medium">{(item.vendedor?.nome || '-').length > 25 ? (item.vendedor?.nome || '-').substring(0, 25) + '...' : (item.vendedor?.nome || '-')}</div>
              {!configuracao.colunas_ativas.includes('fazenda') && (
                <div className="text-gray-800 text-xs font-semibold">{item.fazenda?.nome || item.fazenda_ficticia || ''}</div>
              )}
            </div>
          </td>
        );
      case 'fazenda':
        return (
          <td key={colunaId} className="border border-gray-300 px-2 py-2 text-sm">
            {(() => {
              const faz = item.fazenda?.nome || item.fazenda_ficticia || '-';
              return faz.length > 20 ? faz.substring(0, 20) + '...' : faz;
            })()}
          </td>
        );
      case 'quantidade':
        return <td key={colunaId} className="border border-gray-300 px-1 py-2 text-center">{item._animal ? '1' : (item.qtd || '-')}</td>;
      case 'tipo': {
        const tipoObj = item._animal ? item._animal.tipo_animal : item.tipo_animal;
        let nome = tipoObj?.nome || '-';
        if (nome.toUpperCase().includes('VACA COM CRIA')) {
          nome = nome.toUpperCase().replace('VACA COM CRIA', 'VACA C/ CRIA');
        }
        return <td key={colunaId} className="border border-gray-300 px-2 py-2">{nome}</td>;
      }
      case 'raca': {
        const racaObj = item._animal ? item._animal.raca : item.raca;
        if (!racaObj?.nome) return <td key={colunaId} className="border border-gray-300 px-2 py-2">-</td>;
        const nomeRaca = racaObj.nome.toUpperCase();
        if (!item._animal) {
          return <td key={colunaId} className="border border-gray-300 px-2 py-2">{nomeRaca.length > 12 ? nomeRaca.substring(0, 12) : nomeRaca}</td>;
        }
        const partesRaca = nomeRaca.split(' ');
        const raca = (partesRaca.length > 1 && nomeRaca.length > 14) ? `${partesRaca[0][0]}. ${partesRaca.slice(1).join(' ')}` : nomeRaca;
        return <td key={colunaId} className="border border-gray-300 px-2 py-2">{raca}</td>;
      }
      case 'pai':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-xs">{item._animal?.pai?.trim() ? item._animal.pai.toUpperCase() : '-'}</td>;
      case 'mae':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-xs">{item._animal?.mae?.trim() ? item._animal.mae.toUpperCase() : '-'}</td>;
      case 'data_nascimento':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-center">{item._animal?.data_nascimento ? new Date(item._animal.data_nascimento).toLocaleDateString('pt-BR') : '-'}</td>;
      case 'animal': {
        if (!item._animal) return <td key={colunaId} className="border border-gray-300 px-2 py-2">-</td>;
        const nome = (item._animal.nome || '-').toUpperCase();
        const pct = parseFloat(item._animal.percentual_venda ?? 100);
        const linha1 = Math.abs(pct - 100) > 0.001 ? `${formatarPercentual(pct)}% ${nome}` : nome;
        const registroComoColuna = configuracao.colunas_ativas.includes('registro');
        const registro = !registroComoColuna ? (item._animal.registro || '').trim() : '';
        return (
          <td key={colunaId} className="border border-gray-300 px-2 py-2">
            <span className="font-bold">{linha1}</span>
            {registro && (<><br /><span className="font-bold text-gray-500" style={{ fontSize: '0.68rem' }}>{registro}</span></>)}
          </td>
        );
      }
      case 'sexo': {
        const sexo = item._animal ? (SEXO_LABEL[item._animal.sexo] || item._animal.sexo) : item.sexo;
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-center">{sexo === 'Macho' ? 'M' : sexo === 'Fêmea' ? 'F' : sexo || '-'}</td>;
      }
      case 'idade':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-center">{item._animal ? idadeLabel(item._animal.data_nascimento) : (item.idade?.nome || '-')}</td>;
      case 'peso':
        return (
          <td key={colunaId} className="border border-gray-300 px-2 py-2 text-center">
            {(() => {
              const peso = item._animal ? item._animal.peso : (item.peso_medio || item.peso);
              if (!peso) return '-';
              const pesoNum = parseFloat(peso);
              if (modoPeso === 'peso_arroba' && pesoNum > 100) {
                const arrobas = (pesoNum / 30).toFixed(1);
                return `${peso} - ${arrobas}@`;
              }
              const pesoStr = peso.toString();
              return pesoStr.length > 4 ? parseFloat(peso).toFixed(1) : peso;
            })()}
          </td>
        );
      case 'curral':
        return <td key={colunaId} className="border border-gray-300 px-1 py-2 text-center">{item.curral || '-'}</td>;
      case 'observacoes':
        // Sem override de tamanho/wrap: no PDF impresso herda `td { font-size:
        // 10pt; uppercase }` do CatalogoBase, igual ao Catálogo de Leilão normal
        // (que só define whiteSpace/wordBreak nesta coluna). `text-xs` é inerte
        // na impressão (Tailwind não é carregado lá) e só afeta o preview.
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-xs" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{getObsTexto(item)}</td>;
      case 'valor_inicial':
        return <td key={colunaId} className="border border-gray-300 px-1 py-2 text-center text-xs">{item.valor_minimo_unidade ? `R$ ${parseFloat(item.valor_minimo_unidade).toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : '-'}</td>;
      case 'pelagem':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2">{item._animal?.pelagem?.toUpperCase() || '-'}</td>;
      case 'registro':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2">{item._animal?.registro?.trim() ? <span className="font-bold">{item._animal.registro.toUpperCase()}</span> : '-'}</td>;
      case 'percentual_venda':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2 text-center">{item._animal ? `${formatarPercentual(parseFloat(item._animal.percentual_venda ?? 100))}%` : '-'}</td>;
      case 'localizacao':
        return <td key={colunaId} className="border border-gray-300 px-2 py-2">{item._animal?.localizacao?.toUpperCase() || '-'}</td>;
      default:
        return <td key={colunaId} className="border border-gray-300 px-2 py-2">-</td>;
    }
  };

  const lotesPrimeiraPagina = configuracao.lotes_por_pagina || 32;
  const lotesProximasPaginas = lotesPrimeiraPagina + 5;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="print-area">
      <CatalogoBase
        printStyleOverrides={`
          table { font-size: 14pt; }
          th { font-size: 13pt; padding: 6pt; }
          td { font-size: 14pt; padding: 6pt; }
          .observacoes-col { font-size: 10pt; }
        `}
        titulo="CATÁLOGO DETALHADO"
        subtitulo={leilao ? (
          <div>
            <div>{leilao.nome}</div>
            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 text-gray-600 text-sm sm:text-base mt-2">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {new Date(leilao.data_leilao).toLocaleDateString('pt-BR')}
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                {leilao.cidade}, {leilao.uf}
              </div>
            </div>
          </div>
        ) : null}
        onVoltar={() => window.history.back()}
        logoOrganizador={organizadorAtivo?.logo_url}
        bannerLeilao={leilao?.banner_url ? (leilao.banner_url.startsWith('http') ? leilao.banner_url : `${(import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '')}${leilao.banner_url}`) : null}
        landscape={false}
        showHeaderInfo={false}
        leiloes={leiloes}
        selectedLeilao={leilaoId}
        onLeilaoChange={handleLeilaoChange}
        extraButtons={
          leilaoId && organizadorAtivo?.id ? (
            <CatalogoLeilaoButton
              leilaoId={leilaoId}
              organizadorId={organizadorAtivo.id}
              leilaoNome={leilao?.nome || ''}
              label="Imprimir"
              endpoint="catalogo-detalhado"
              permitirPaisagem
            />
          ) : null
        }
      >
        {/* No celular a tabela tem colunas demais pra ficar legível — orienta a usar Imprimir */}
        <div className="sm:hidden text-center py-10 px-4 text-gray-500">
          <Printer size={32} className="mx-auto mb-3 text-gray-400" />
          <p className="font-medium text-gray-700">Visualização em tabela não disponível no celular</p>
          <p className="text-sm mt-1">Use o botão <strong>Imprimir</strong> no topo da página para gerar o PDF do catálogo.</p>
        </div>

        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                {colunasTabela.map(coluna => (
                  <th key={coluna.id} className={`border border-gray-300 px-1 py-2 text-center font-semibold text-sm ${coluna.width}`}>
                    {coluna.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhasExpandidas.length > 0 ? (
                linhasExpandidas.map((item, index) => {
                  const destaque = temDestaque(item);
                  const bg = destaque ? 'bg-amber-100' : (corPorLote[item.id] ? 'bg-white' : 'bg-gray-50');
                  const primeiraLinhaDoLote = index === 0 || linhasExpandidas[index - 1].id !== item.id;
                  const textoAbaixo = item._animal ? obsMaisLocalizacao(item) : (obsSempreAbaixo ? getObsTexto(item) : null);
                  const mostrarAbaixo = item._animal ? !!textoAbaixo : (primeiraLinhaDoLote && obsSempreAbaixo && textoAbaixo !== '-');
                  return (
                  <React.Fragment key={`${item.id}-${item._animal?.id ?? 0}`}>
                    <tr className={`${bg} ${destaque ? 'font-bold' : ''}`}>
                      {colunasTabela.map(coluna => renderCelula(item, coluna.id))}
                    </tr>
                    {mostrarAbaixo && (
                      <tr className={corPorLote[item.id] ? 'bg-white' : 'bg-gray-50'}>
                        <td colSpan={colunasTabela.length} className="border border-gray-300 px-2 pb-2 pt-0 text-sm text-gray-700" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {textoAbaixo}
                        </td>
                      </tr>
                    )}
                    {(() => {
                      if (index + 1 === lotesPrimeiraPagina) {
                        return <tr className="page-break-after"><td colSpan={colunasTabela.length} style={{height: '0px', padding: '0px', border: 'none'}}></td></tr>;
                      } else if (index + 1 > lotesPrimeiraPagina && (index + 1 - lotesPrimeiraPagina) % lotesProximasPaginas === 0) {
                        return <tr className="page-break-after"><td colSpan={colunasTabela.length} style={{height: '0px', padding: '0px', border: 'none'}}></td></tr>;
                      }
                      return null;
                    })()}
                  </React.Fragment>
                );})
              ) : (
                <tr>
                  <td colSpan={colunasTabela.length} className="border border-gray-300 px-3 py-8 text-center text-gray-500">
                    Nenhum lote encontrado na ordem de entrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {configuracao.mostrar_totais && (
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-item">
                <div className="total-number text-gray-900">{lotes.length}</div>
                <div className="total-label">Total de Lotes</div>
              </div>
              <div className="total-item">
                <div className="total-number text-blue-600">
                  {linhasExpandidas
                    .filter(l => (l._animal ? SEXO_LABEL[l._animal.sexo] : l.sexo) === 'Macho')
                    .reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
                </div>
                <div className="total-label">Machos</div>
              </div>
              <div className="total-item">
                <div className="total-number text-pink-600">
                  {linhasExpandidas
                    .filter(l => (l._animal ? SEXO_LABEL[l._animal.sexo] : l.sexo) === 'Fêmea')
                    .reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
                </div>
                <div className="total-label">Fêmeas</div>
              </div>
              <div className="total-item">
                <div className="total-number text-green-600">
                  {linhasExpandidas.reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
                </div>
                <div className="total-label">Total de Animais</div>
              </div>
            </div>
          </div>
        )}

        <div className="footer-final">
          www.rematei.com.br | Gerado em {new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
        </div>
      </CatalogoBase>
    </div>
  );
};

export default CatalogoDetalhado;
